import { PlusCircleOutlined, SafetyCertificateOutlined, SearchOutlined, UserOutlined } from "@ant-design/icons";
import { Button, Input, Modal, notification, Select, Transfer } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { useContext, useEffect, useState } from "react";
import { AiOutlineDesktop, AiOutlineMobile, AiOutlineTablet } from "react-icons/ai";
import { BsAndroid, BsApple, BsBrowserChrome, BsBrowserEdge, BsBrowserFirefox, BsBrowserSafari, BsRobot, BsShieldLockFill, BsWindows } from "react-icons/bs";
import { FaRegEdit } from "react-icons/fa";
import { RiDeleteBinLine } from "react-icons/ri";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { AppContext } from "../AppContext";
import InfoLabel from "../components/InfoLable";
import NotificationStatus from "../components/NotificationStatus";
import tr from "../i18n";
import { hasAccess, hasRoles, ROLES } from "../utils/AccessLevels";
import { transformTurkish, validEmail, validPhoneNumber } from "../utils/RegExUtility";

const { Option } = Select;
dayjs.extend(customParseFormat);

const settings = localStorage.getItem("settings");
const parsedSettings = (() => {
  try { return JSON.parse(settings); } catch { return null; }
})();
const isUS = parsedSettings?.origin === "us";

const USER_TYPES = {
  TECHNICAL_ADMIN: "technical_admin",
  ...(!isUS && { REGIONAL_MANAGER: "regional_manager" }),
  FIELD_MANAGER: "field_manager",
  ...(!isUS && { PRO_USER: "pro_user" }),
  END_USER: "end_user",
  DEMO: "demo",
  GARDEN_TESTER: "garden_tester",
};

const getRoleOptions = ({ role, tenantType, isUS }) => [
  hasAccess(ROLES.TECHNICAL_ADMIN, role) && (
    <Select.Option key="technical_admin" value={USER_TYPES.TECHNICAL_ADMIN}>{tr("Technical Admin")}</Select.Option>
  ),
  !isUS && hasAccess(ROLES.TECHNICAL_ADMIN, role) && (
    <Select.Option key="regional_manager" value={USER_TYPES.REGIONAL_MANAGER}>{tr("Regional Manager")}</Select.Option>
  ),
  hasAccess(ROLES.REGIONAL_MANAGER, role) && (
    <Select.Option key="field_manager" value={USER_TYPES.FIELD_MANAGER}>{tr("Field Manager")}</Select.Option>
  ),
  hasAccess(ROLES.FIELD_MANAGER, role) && tenantType === "pro" && (
    <Select.Option key="end_user" value={USER_TYPES.END_USER}>{tr("End User")}</Select.Option>
  ),
  hasAccess(ROLES.FIELD_MANAGER, role) && (
    <Select.Option key="garden_tester" value={USER_TYPES.GARDEN_TESTER}>{tr("Garden Tester")}</Select.Option>
  ),
  !isUS && hasRoles([ROLES.SUPER_ADMIN, ROLES.TECHNICAL_ADMIN], role) && (
    <Select.Option key="demo" value={USER_TYPES.DEMO}>{tr("Observer")}</Select.Option>
  ),
].filter(Boolean);

const UserAccess = () => {
  const [usersResult, setUsersResult] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [editableUser, setEditableUser] = useState(null);
  const [targetKeys, setTargetKeys] = useState([]);
  const [userEditModalVisibility, setUserEditModalVisibility] = useState(false);

  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const { gardens, fetchAllGardens, role } = useContext(AppContext);
  const queryClient = useQueryClient();
  const [showConfirmDeleteModal, setShowModal] = useState(false);
  const [showUserCreateModal, setShowUserCreateModal] = useState(false);

  const [invitationCountryCode, setInvitationCountryCode] = useState("+1");
  const [invitationPhone, setInvitationPhone] = useState(null);
  const [invitationRole, setInvitationRole] = useState(null);
  const [invitationName, setInvitationName] = useState(null);
  const navigate = useNavigate();
  const tenantType = localStorage.getItem("type");

  useEffect(() => {
    if (!role) return;
    try {
      if (!hasAccess(ROLES.FIELD_MANAGER, role)) {
        navigate("/control-center/gardens");
      }
    } catch (error) {
      console.error(error);
    }
  }, [navigate, role]);

  const users = useQuery(
    "FetchAllUsers",
    async () => {
      const res = await axios.get(process.env.REACT_APP_GARDENER_API_END_POINT + "/user");
      return res.data.data;
    },
    { refetchOnWindowFocus: false },
  );

  useEffect(() => {
    console.count("UsersAccess useEffect - select default user");
    if (users.isSuccess && users.data && selectedUser === null) {
      try {
        const accessibleUsers = users.data.filter((t) => {
          if (hasAccess(ROLES.SUPER_ADMIN, role)) return true;
          return hasAccess(t.role, role);
        });
        if (accessibleUsers.length > 0) {
          const defaultUser = accessibleUsers[0];
          setSelectedUser(defaultUser);
          setEditableUser(defaultUser);
        }
      } catch (error) {
        if (users.data.length > 0) {
          const defaultUser = users.data[0];
          setSelectedUser(defaultUser);
          setEditableUser(defaultUser);
        }
        console.error(`Error selecting user: ${error}`);
      }
    }
  }, [users.isSuccess, users.data, selectedUser, role, tenantType]);

  useEffect(() => {
    console.count("UsersAccess useEffect - fetch gardens and tags");
    const fetchGardenTags = async () => {
      try {
        // setLoadingGardens(true)
        const res = await axios.get(process.env.REACT_APP_PLANNER_API_END_POINT + "/tag?resource=gardens");
        setTags(res.data.tags);
      } catch (error) {
        console.log(error);
      } finally {
        // setLoadingGardens(false)
      }
    };

    fetchAllGardens();
    fetchGardenTags();
  }, [fetchAllGardens]);

  useEffect(() => {
    const fetchUserGardens = async () => {
      if (!selectedUser) return;

      try {
        setLoading(true);
        const res = await axios.get(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/user/${selectedUser.id}`);
        const gardenIds = res.data.gardens.map((g) => g.id);
        setTargetKeys(gardenIds);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserGardens();
  }, [selectedUser]);

  const onTargetsChange = async (nextTargetKeys, direction, moveKeys) => {
    const userId = selectedUser.id;
    if (direction === "right") {
      // adding gardens : moveKeys
      try {
        setLoading(true);

        const res = await axios.put(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/user`, {
          userId,
          gardenIds: moveKeys,
        });
        if (res.data.success) {
          notification.success({
            message: tr("Success"),
            description: tr("Gardens assigned successfully"),
            placement: "topRight",
          });

          // if successful set target keys
          setTargetKeys(nextTargetKeys);
        } else {
          notification.error({
            message: tr("Something went wrong"),
            description: tr(`While assigning gardens`),
            placement: "topRight",
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }

    if (direction === "left") {
      // removing gardens : moveKeys

      try {
        setLoading(true);
        const res = await axios.delete(`${process.env.REACT_APP_PLANNER_API_END_POINT}/garden/user`, {
          data: {
            userId,
            gardenIds: moveKeys,
          },
        });

        if (res.data.success) {
          notification.success({
            message: tr("Success"),
            description: tr("Gardens unassigned successfully"),
            placement: "topRight",
          });

          // if successful set target keys
          setTargetKeys(nextTargetKeys);
        } else {
          notification.error({
            message: tr("Something went wrong"),
            description: tr(`While removing gardens`),
            placement: "topRight",
          });
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
  };

  const onGardenSearch = (dir, value) => { };

  const onUserClick = (user) => {
    setSelectedUser(user);
    setEditableUser(user);
  };

  const getNameFromEmail = (email) => email.split("@")[0];

  const filterOption = (input, item) => {
    return RegExp(`.*${transformTurkish(input)}.*`, "gi").test(transformTurkish(item.geoJson.properties.MAHAL_ADI));
  };

  const filteredGardens = () => {
    if (selectedTags.length > 0) {
      return gardens.filter((g) => selectedTags.every((t) => g.tags && g.tags.includes(t)));
    }

    return gardens;
  };

  const onUserSearch = (e) => {
    const value = e.target.value;
    setUserSearchQuery(value);
    const result = users.data.filter((u) =>
      RegExp(`.*${transformTurkish(value.toLowerCase())}.*`, "gi").test(transformTurkish(u.email.toLowerCase() + u.phone.toLowerCase() + (u.name || "").toLowerCase())),
    );

    setUsersResult(result);
  };

  const onTagsChange = (e) => {
    setSelectedTags(e);
  };

  /**
   * Delete user
   */
  const deleteUser = useMutation(
    (userId) => {
      return axios.delete(`${process.env.REACT_APP_GARDENER_API_END_POINT}/user`, { data: { userId } });
    },
    {
      onSuccess: () => {
        notification.success({
          message: tr("Successfully removed user"),
          description: ``,
          placement: "topRight",
        });

        queryClient.invalidateQueries("FetchAllUsers");
      },
      onError: () => {
        notification.error({
          message: tr("Failed to remove user"),
          description: ``,
          placement: "topRight",
        });
      },
      onSettled: () => {
        setShowModal(false);
      },
    },
  );

  const [sessionManagementVisible, setSessionManagementVisible] = useState(false);

  const getOs = (ua) => {
    if (!ua) return "Legacy|";

    switch (ua.os.name) {
      case "Windows":
        return <BsWindows />;

      case "OSX":
        return <BsApple />;

      case "IOS":
        return <BsApple />;

      case "Android":
        return <BsAndroid />;

      default:
        return ua.os.name;
    }
  };

  const getBrowser = (ua) => {
    if (!ua) return "N/A";

    switch (ua.browser.name) {
      case "Chrome":
        return <BsBrowserChrome />;

      case "Safari":
        return <BsBrowserSafari />;

      case "Firefox":
        return <BsBrowserFirefox />;

      case "Edge":
        return <BsBrowserEdge />;

      default:
        return ua.browser.name;
    }
  };

  const getDeviceType = (ua) => {
    if (!ua) return "N/A";

    if (ua.isMobile) {
      return <AiOutlineMobile />;
    }

    if (ua.isTablet) {
      return <AiOutlineTablet />;
    }

    if (ua.isDesktop) {
      return <AiOutlineDesktop />;
    }

    if (ua.isBot) {
      return <BsRobot />;
    }
  };

  const logoutUser = useMutation(
    ({ id, iat, ua }) => {
      return axios.post(`${process.env.REACT_APP_GARDENER_API_END_POINT}/auth/logout`, {
        id,
        iat,
        ua,
      });
    },
    {
      onSuccess: () => {
        notification.success({
          message: tr("User logged out successfully"),
        });
      },
      onError: (error) => {
        notification.error({
          message: tr("Something went wrong while logging out user"),
        });
      },
    },
  );

  const moveUser = useMutation(
    () => {
      return axios.put(`${process.env.REACT_APP_PLANNER_API_END_POINT}/user/move`, {
        userId: editableUser.id,
        tenantId: editableUser.tenantId,
        role: editableUser.role,
        name: editableUser.name,
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("FetchAllUsers");
        notification.success({
          message: tr("User info updated successfully"),
        });
      },
      onError: (error) => {
        notification.error({
          message: tr("An error occurred"),
        });
      },
    },
  );

  const sendInvitation = async () => {
    if (!invitationPhone || !invitationRole || !invitationCountryCode) return;

    if (!validEmail.test(invitationPhone) && !validPhoneNumber.test(invitationPhone)) {
      return notification.error({
        message: tr("Check the information you entered"),
        description: tr(`Faulty phone number`),
        placement: "topRight",
      });
    }

    const res = await axios.post(process.env.REACT_APP_GARDENER_API_END_POINT + "/user/create", {
      emailOrPhone: getInviteFullPhone(),
      role: invitationRole,
      name: invitationName,
    });

    if (res.data.success) {
      setInvitationPhone(null);
      setInvitationName(null);
      setInvitationRole(null);
      notification.success({
        message: tr("User created successfully"),
        description: tr("Invitation has been sent to the user"),
        placement: "topRight",
      });
      queryClient.invalidateQueries("FetchAllUsers");
    } else {
      notification.error({
        message: tr("User creation failed"),
        description: tr("An error occurred"),
        placement: "topRight",
      });
    }
  };

  const onInvitationRoleChange = (e) => {
    setInvitationRole(e);
  };

  const onInvitationPhoneChange = (e) => {
    const value = e.target.value;
    setInvitationPhone(value);
  };

  const onInvitationNameChange = (e) => {
    const value = e.target.value;
    setInvitationName(value);
  };

  // Combined phone number (without the "+" prefix)
  const getInviteFullPhone = () => {
    return `${invitationCountryCode.replace(/^\+/, "")}${invitationPhone}`;
  };

  // const [invitationPhone, setInvitationPhone] = useState(null)

  return (
    <>
      <div className="flex flex-col md:flex-row bg-transparent text-gray-800 rounded-xl gap-4 font-sans h-full">
        <div
          className="relative flex flex-col w-full md:w-1/4 rounded-xl bg-white overflow-hidden"
          style={{ height: "calc(100vh - 48px)" }}
        >
          <div className="w-full p-5">
            <Input style={{ borderRadius: 5 }} onChange={onUserSearch} prefix={<SearchOutlined />} placeholder={tr("Search for users")} allowClear />
          </div>

          {/* Scrollable User List */}
          <div className="w-full px-3 flex-1 overflow-y-auto pb-24 min-h-0">
            <div className="flex flex-col">
              {users.isLoading && (
                <>
                  <div className="p-2 animate-pulse flex flex-row ">
                    <div className="bg-gray-200 h-5 w-5 mr-2 rounded-full"></div>
                    <div className="bg-gray-200 h-5 w-full rounded-md"></div>
                  </div>
                  <div className="p-2 animate-pulse flex flex-row ">
                    <div className="bg-gray-200 h-5 w-5 mr-2 rounded-full"></div>
                    <div className="bg-gray-200 h-5 w-full rounded-md"></div>
                  </div>
                </>
              )}

              {users.isSuccess &&
                (userSearchQuery ? usersResult : users.data)
                  .filter((user) => {
                    if (hasAccess(ROLES.SUPER_ADMIN, role)) return true;
                    return hasAccess(user.role, role);
                  })
                  .map((user) => (
                    <div
                      key={user.id}
                      onClick={() => onUserClick(user)}
                      className={`flex flex-row gap-4 items-center rounded-lg hover:bg-gray-200 p-2 cursor-pointer mb-1 ${selectedUser?.id === user.id ? "bg-gray-200" : ""
                        }`}
                    >
                      <div>
                        <span className="block font-semibold">{user.name || formatPhoneNumber(user.phone)}</span>
                        <span className="text-xs block">{formatPhoneNumber(user.phone)}</span>
                      </div>
                    </div>
                  ))}
            </div>
          </div>

          <div className="absolute bottom-0 left-0 w-full p-4 bg-white border-t">
            <div className="mx-auto max-w-sm">
              <button
                className="flex flex-row gap-2 w-full h-9 rounded-xl text-sm font-semibold text-black border items-center justify-center"
                onClick={() => setShowUserCreateModal(true)}
              >
                <PlusCircleOutlined size={30} />
                {tr("Add New User")}
              </button>
            </div>
          </div>
        </div>
        <div className="bg-white flex flex-col h-full md:w-3/4 rounded-xl">
          {selectedUser && (
            <div className="relative flex flex-row gap-4 items-center p-4 border-b w-full flex-wrap">
              <div className="">
                <div className="flex items-center relative gap-4">
                  <div className="block whitespace-nowrap font-bold text-lg">{selectedUser.name || selectedUser.email || selectedUser.phone}</div>
                  {!["demo", "super_admin"].includes(selectedUser.role) && (
                    <div className="items-center rounded-lg text-center bg-blue-300 px-2 py-1">{tr(selectedUser.role)}</div>
                  )}
                </div>
                <div className="text-xs flex items-start gap-1 flex-col ">
                  <div>{selectedUser.email || selectedUser.phone}</div>{" "}
                </div>
              </div>

              <div className="absolute inset-y-0 right-0 h-16 m-2 flex flex-row gap-3">
                {hasAccess(ROLES.FIELD_MANAGER, role) && (
                  <>
                    <FaRegEdit
                      className="cursor-pointer hover:text-blue-500"
                      onClick={() => {
                        setUserEditModalVisibility(true);
                      }}
                      size={20}
                    />
                    <RiDeleteBinLine className="cursor-pointer" size={18} onClick={() => setShowModal(true)} />
                  </>
                )}
              </div>
            </div>
          )}
          <div className="flex gap-4 p-4 px-6 flex-wrap">
            {!selectedUser && (
              <div className="flex p-4 border rounded-md  gap-4 ">
                <InfoLabel>{tr("Please select a user")}</InfoLabel>
              </div>
            )}

            <div className="w-full  pb-4 mt-0">
              <span className="block font-bold">{tr("Park/Garden Access")}</span>
            </div>
            {selectedUser && (
              <div className="flex flex-col w-full gap-1">
                <InfoLabel> {tr("Notification SMS status")}</InfoLabel>

                <NotificationStatus userId={selectedUser.id} />
              </div>
            )}
            {!selectedUser && (
              <div className="flex p-4 border rounded-md  gap-4 ">
                <InfoLabel>{tr("Please select a user")}</InfoLabel>
              </div>
            )}
            <div className="w-full flex flex-row">
              {loading && (
                <div className="w-full flex flex-col gap-5 animate-pulse">
                  <div className="w-full flex flex-row gap-5">
                    <div className="flex flex-col w-1/3 rounded-md gap-5">
                      <div className="bg-gray-200 h-5 rounded-md"></div>
                      <div className="bg-gray-200 h-32 rounded-md"></div>
                    </div>
                    <div className="flex flex-col w-1/3 rounded-md gap-5">
                      <div className="bg-gray-200 h-5 rounded-md"></div>
                      <div className="bg-gray-200 h-32 rounded-md"></div>
                    </div>
                  </div>
                </div>
              )}
              {selectedUser && !loading && (
                <div className="flex flex-col gap-3 h-full w-full">
                  <div className="flex flex-col gap-1 rounded">
                    <InfoLabel>{tr("Filter gardens by tags")}</InfoLabel>
                    <Select className="" mode="tags" placeholder={tr("Filter gardens by tags")} onChange={onTagsChange}>
                      {tags.map((t) => (
                        <Option key={t.id}>{t.name}</Option>
                      ))}
                    </Select>
                  </div>
                  <div className="w-full h-full m-1">
                    <Transfer
                      dataSource={filteredGardens()}
                      showSearch
                      filterOption={filterOption}
                      targetKeys={targetKeys}
                      onChange={onTargetsChange}
                      onSearch={onGardenSearch}
                      render={(item) => item.name}
                      rowKey={(r) => r.id}
                      listStyle={{ width: 410, height: "50vh", maxHeight: "50vh", borderRadius: 20, overflow: "hidden" }}
                      locale={{ itemUnit: tr("item"), itemsUnit: tr("items"), searchPlaceholder: tr("Search") }}
                      titles={[
                        <div className="text-gray-800 font-bold text-xs">
                          {tr("Suitable")}
                          <span className="text-gray-500 text-xs block font-normal">{tr("Assignable Parks/Gardens")}</span>
                        </div>,
                        <div className="text-gray-800 font-bold text-xs">
                          {tr("Appointed")}
                          <span className="text-gray-500 text-xs block font-normal">{tr("Parks/Gardens Assigned to the User")}</span>
                        </div>,
                      ]}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        width={window.innerWidth < 768 ? "50%" : "30%"}
        bodyStyle={{ maxHeight: "80vh", overflowY: "auto" }}
        title={tr("Edit User Info")}
        visible={userEditModalVisibility}
        onOk={() => {
          if (editableUser?.name && editableUser?.role && editableUser?.id && editableUser?.tenantId) {
            moveUser.mutate();
            setUserEditModalVisibility(false);
          }
        }}
        okText={tr("Save")}
        cancelButtonProps={{ style: { display: "none" } }}
        onCancel={() => {
          setUserEditModalVisibility(false);
        }}
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <span>{tr("Name")}</span>
            <Input
              value={editableUser?.name}
              onChange={(e) => {
                setEditableUser((p) => ({ ...p, name: e.target.value }));
              }}
              placeholder={tr("Name")}
              style={{ borderRadius: 5 }}
            ></Input>
          </div>
          <div className="flex flex-col gap-1">
            <span>{tr("User Role")}</span>
            <Select
              value={editableUser?.role}
              dropdownStyle={{ borderRadius: 5 }}
              suffixIcon={<SafetyCertificateOutlined />}
              className="rounded-md"
              placeholder={tr("User Role")}
              onChange={(e) => {
                setEditableUser((p) => ({ ...p, role: e }));
              }}
            >
              {getRoleOptions({ role, tenantType, isUS })}
            </Select>
          </div>
        </div>
      </Modal>

      {showConfirmDeleteModal && hasAccess(ROLES.FIELD_MANAGER, role) ? (
        <>
          <div className="justify-center items-center font-sans  flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
            <div className="relative w-auto my-6 mx-auto max-w-3xl">
              {/*content*/}
              <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                {/*header*/}
                <div className="flex items-center justify-center p-5 mt-9 ml-5 ">
                  <h6 className=" text-center text-2xl font-semibold w-96">{tr("Delete User Prompt")}</h6>
                  <button
                    className="p-1 ml-auto bg-transparent border-0 text-black opacity-5 float-right text-3xl leading-none font-semibold outline-none focus:outline-none"
                    onClick={() => setShowModal(false)}
                  >
                    <span className="bg-transparent text-black opacity-5 h-6 w-6 text-2xl block outline-none focus:outline-none">×</span>
                  </button>
                </div>
                {/*body*/}
                <div className="w-full items-center  flex-auto">
                  <p className="text-center w-full text-black">{tr("This change cannot be reverted")}</p>
                </div>
                {/*footer*/}
                <div className="flex items-center justify-end p-4 gap-3">
                  <button
                    className=" font-bold rounded-full border-2 w-48 px-6 py-3 text-sm text-black outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                    type="button"
                    onClick={() => setShowModal(false)}
                  >
                    {tr("Close")}
                  </button>
                  <button
                    className="bg-green-brand text-white text-sm w-48 px-6 py-3 rounded-full shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                    type="button"
                    onClick={() => deleteUser.mutate(selectedUser.id)}
                  >
                    {tr("Delete User")}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      ) : null}

      <Modal visible={showUserCreateModal}
        onCancel={() => setShowUserCreateModal(false)}
        footer={null}
        className="rounded-xl"
        bodyStyle={{ padding: 0 }}
        width={500}
        style={{ maxWidth: "95vw" }}>
        <div className="p-5 bg-white w-full text-gray-800">
          <div className="w-full items-center py-2">
            <span className="font-bold text-center text-2xl">{tr("Add User")}</span>
            <div className="my-2"></div>
            <InfoLabel>{tr("Invite user with phone number")}</InfoLabel>
          </div>
          <div className="flex flex-col gap-5 flex-wrap w-full">
            <Input
              className="rounded-full"
              style={{ borderRadius: 10 }}
              value={invitationName}
              type="text"
              placeholder={tr("Name")}
              prefix={<UserOutlined />}
              onChange={onInvitationNameChange}
            />
            <div style={{ display: "flex" }}>
              <Input
                value={invitationCountryCode}
                onChange={(e) => setInvitationCountryCode(e.target.value)}
                style={{
                  width: 70,
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  textAlign: "center",
                }}
              />

              <Input
                value={invitationPhone}
                onChange={onInvitationPhoneChange}
                placeholder="5xx xxx xx xx"
                style={{
                  flex: 1,
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                }}
              />
            </div>
            {/* <Input type="text" placeholder="Phone" className="" style={{ width: 350, borderRadius: 5 }} suffix={<SafetyCertificateOutlined className="" />} onChange={onInvitationPhoneChange} /> */}
            <Select
              value={invitationRole}
              dropdownStyle={{ borderRadius: 5 }}
              suffixIcon={<SafetyCertificateOutlined />}
              className="rounded-md"
              placeholder={tr("User Role")}
              onChange={onInvitationRoleChange}
            >
              {getRoleOptions({ role, tenantType, isUS })}
            </Select>
            <div className="flex flex-col md:flex-row items-center w-full gap-3">
              <Button
                type="button"
                style={{ fontWeight: "bold" }}
                className="w-full rounded-full"
                onClick={() => {
                  setShowUserCreateModal(false);
                }}
              >
                {tr("Cancel")}
              </Button>
              <Button
                type="button"
                style={{ backgroundColor: "#0093A7", fontWeight: "bold", color: "white" }}
                className="w-full rounded-full text-white bg-green-brand"
                onClick={sendInvitation}
              >
                {tr("Send invitation")}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

const formatPhoneNumber = (phone) => {
  const locale = phone.startsWith("90") ? "TR" : "US";
  const phoneNumber = parsePhoneNumberFromString(phone, locale);
  return phoneNumber ? phoneNumber.formatInternational() : phone;
};

export default UserAccess;
